/**
 * @version 1.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/пока нет documentation
 */

/**
 * DEPENDENCIES
 */
import 'url-search-params-polyfill';
import axios from 'axios';
import Cluster from './cluster';
import Controls from './controls';
import mapUtils from './utils';
import Marker from './marker';
import Observer from '../../common/scripts/observer';
// import ScrollBy from '../scrollBy';

const observer = new Observer();
// const scrollBy = new ScrollBy();

class YandexMap {
    constructor(ymaps) {
        /**
         * Экземпляр загруженных яндекс карт.
         */
        this.ymaps = ymaps;

        /**
         * Экземпляр конструктора геообъектов
         */
        this.geoObjectCollection = new this.ymaps.GeoObjectCollection();

        /**
         * Все настроки карты
         * @type {Object}
         */
        this.settings = {};

        /**
         * Яндексовый объект карты
         * @type {null}
         */
        this.map = null;

        /**
         * URL по которому будет выполнен ajax запрос за данными
         */
        this.url = '';

        /**
         * Класса DOM-элемента в который будет вставлена карта
         */
        this.baseClass = '.j-yandex-map-base';

        /**
         * Класса DOM-элемента прелоадера
         */
        this.preloaderClass = '.j-yandex-map-preloader';

        /**
         * Брейкпоинт после которого маркерты открываются не по клику, а по ховеру.
         * @type {number}
         */
        this.desktopBreakpoint = 1280;

        /**
         * Список координат всех элементов на карте
         */
        this.destinations = {};
    }

    /**
     * Метод принимает настройки из вне и запускает процесс инициализации карт
     * @param {object} outerOptions - опции из скрипта
     */
    init(outerOptions) {
        this.outerOptions = outerOptions;
        this.mapWrapper = outerOptions.wrapper;
        this.content = this.mapWrapper.querySelector(this.baseClass);
        this.id = this.content.id;
        this.url = this.mapWrapper.dataset.ajax;
        this.json = this.mapWrapper.dataset.json;

        this._runMap();
        this._subscribes();
    }

    /**
     * Метод содержит в себе колбэки на события других модулей
     */
    _subscribes() {
        this._showGeoObject = this._showGeoObject.bind(this);
        this._hideGeoObject = this._hideGeoObject.bind(this);
        this._showAllObjects = this._showAllObjects.bind(this);

        observer.subscribe('addFilterItem', (markerType) => {
            this.cluster.removeAllGeoObject();
            this._toggleGeoObjects(markerType, this._showGeoObject);
        });

        observer.subscribe('removeFilterItem', (markerType) => {
            this._toggleGeoObjects(markerType, this._hideGeoObject);
        });

        observer.subscribe('contactsPointerClick', (pointer) => {
            this._moveMap(pointer);
        });

        observer.subscribe('allFilterItem', this._showAllObjects);
    }

    /**
     * Основной метод, который запускает выполнение всех остальных методов. При ошибке кладет весь остальной процесс.
     * @private
     */
    async _runMap() {
        await this._setSettings();
        await this._initYandexMap();
        this._initMarker();
        this._initControls();
        this._initCluster();
        this._showMap();

        if (this.settings.polygon.length) {
            this._initPolygon();
        }

        observer.publish('yandexmapReady');
    }

    /**
     * Метод собирает все настройки в единый объект. Если нет ответа от сервера, карта все равно будет работать
     * с дефолтными настройками
     * @private
     */
    async _setSettings() {
        this._connectSettings(this._setDefaultSettings());
        this._connectSettings(this._setScriptSettings());
        this._preBody();

        if (this.url) {
            try {
                const response = await axios.post(this.url, this.body);

                this._connectSettings(response.data.data);
            } catch (err) {
                console.error(`Не удалось получить данные от сервера, карта работает с дефолтными настройками ${err}`);
            }

            return;
        }

        if (this.json) {
            const decode = JSON.parse(atob(this.json));

            this._connectSettings(decode.data);
        }
    }

    /**
     * Метод запускает создание экземпляра яндекс карт. Здесь происходит непосредственная вставка карты на страницу.
     * При ошибке выбросит исключение и положит весь остальной процесс. Т.к при ошибке инита нет смысла выполнять
     * дальнейшие методы
     * @return {Promise<void>} обещание на инициализацию карт
     * @private
     */
    async _initYandexMap() {
        const insertMap = () => {
            return new Promise((resolve) => {
                this.map = new this.ymaps.Map(this.id, this.settings, {suppressMapOpenBlock: true});

                if (this.map) {
                    resolve();
                } else {
                    throw new Error();
                }
            });
        };

        try {
            return await insertMap();
        } catch (err) {
            console.error(`При инициализации Яндекс Карт произошла критическая ошибка ${err}`);
            throw new Error();
        }
    }

    /**
     * Склеивает настройки из одного источника в общую настройку всей карты.
     * @param {object} outerSettings - настройки из вне.
     * @private
     */
    _connectSettings(outerSettings) {
        if (typeof outerSettings === `object`) {
            Object.assign(this.settings, outerSettings);
        }
    }

    /**
     * Устаналивает настройки по умолчанию.
     * @return {{
     * center: number[],
     * mapTypeControl: boolean,
     * zoom: number,
     * minZoom: number,
     * maxZoom: number,
     * zoomStep: number,
     * controls: Array,
     * zoomControl: boolean,
     * customZoomControl: boolean,
     * fullScreenControl: boolean,
     * customFullScreenControl: boolean,
     * height: number,
     * zoomScroll: boolean}} - Объект настройки карты
     * @private
     */
    _setDefaultSettings() {
        const latCenter = 59.939014;
        const lngCenter = 30.315545;
        const heightMap = this.content.offsetHeight;
        /* eslint-disable */
        return {
            center                 : [latCenter, lngCenter],
            mapTypeControl         : false,
            autoZoom               : true, // setBounds при загрузке
            zoom                   : 12,
            zoomMargin             : 250, //отступы при setBounds
            minZoom                : 10,
            maxZoom                : 18,
            zoomStep               : 1,
            controls               : [],
            zoomControl            : true,
            customZoomControl      : true,
            fullScreenControl      : true,
            customFullScreenControl: true,
            height                 : heightMap,
            zoomScroll             : false,
            markers                : []
        };
        /* eslint-enabled*/
    }

    /**
     * Возвращает настройки из скрипта - app.js
     * @return {object} - данные указанные в вызове метода init в app.js
     * @private
     */
    _setScriptSettings() {
        return this.outerOptions;
    }

    /**
     * Инициализация маркера на визуальном выборщике.
     */
    _initMarker() {
        this.settings.markers.forEach((item) => {
            const marker = new Marker();
            this.geoObjectCollection.add(marker.createHtmlMarker(item));
            marker.closeBallonOnClickMap(this.map);

            // Если у маркера есть значение target - для перемещения карты к точке - то собираем его координаты и имя(target) в объект
            if (marker.ballon.target) {
                this.destinations[marker.ballon.target] = marker.ballon.coords;
            }
        });

        this.geoObjectCollectionArray = this.geoObjectCollection.toArray().map(x => x);
        this.map.geoObjects.add(this.geoObjectCollection);

        if (this.geoObjectCollectionArray.length > 1 && this.settings.autoZoom) {
            this.map.setBounds(this.geoObjectCollection.getBounds(), {
                zoomMargin: this.settings.zoomMargin
            });
        }

        this._bindMapClick();
    }

    /**
     * Закрытие панели карты
     * @private
     */
    _bindMapClick() {
        const bindMapClick = new CustomEvent('bindMapClick');
        window.dispatchEvent(bindMapClick);

        this.map.events.add('click', () => {
            if (window.innerWidth < this.desktopBreakpoint) {
                window.dispatchEvent(bindMapClick);
            }
        });
    }

    /**
     * Метод устанавливает касмтомные контролы и прочие элементы управления.
     * @private
     */
    _initControls() {
        new Controls().init(this.map, this.settings, this.ymaps);
    }

    /**
     * Метод инициализирует кластеры
     * @private
     */
    _initCluster() {
        if (this.outerOptions.cluster) {
            this.cluster = new Cluster();

            this.cluster.init(this.geoObjectCollection);
        }
    }

    /**
     * Метод скрывает прелоадер и показывает карту по завершению выполнения всех методов.
     * @private
     */
    _showMap() {
        if (this.preloader) {
            this.preloader = this.mapWrapper.querySelector(this.preloaderClass);
            this.preloader.style.opacity = '0';
        }

        this.content.style.opacity = '1';
    }


    /**
     * Подготавливает тело ajax-запроса, в данном случае это гет-параметры, если они есть.
     * @private
     */
    _preBody() {
        const get = window.location.search;

        if (!get) {
            this.body = '';

            return;
        }

        this.body = new FormData();

        const url = new URLSearchParams(get);
        const key = 0;
        const value = 1;

        for (const item of url.entries()) {
            this.body.append(item[key], item[value]);
        }
    }

    /**
     * Отображает геообъекты на карте и в кластерах
     * @param {String} geoObject - тип маркера
     * @private
     */
    _showGeoObject(geoObject) {
        if (this.settings.cluster) {
            this.cluster.addGeoObjectToClusterer(geoObject);
        } else {
            geoObject.options.set('visible', true);
        }
    }

    /**
     * Скрывает геообъекты на карте и в кластерах
     * @param {String} geoObject - объект карты
     * @private
     */
    _hideGeoObject(geoObject) {
        if (this.settings.cluster) {
            this.cluster.removeGeoObjectToClusterer(geoObject);
        } else {
            geoObject.options.set('visible', false);
        }
    }

    _showAllObjects() {
        console.dir(this.cluster)
        this.cluster.showAllGeoObject();
    }

    /**
     * Обёртка для переключения геообъектов по типу
     * @param {String} itemType - тип геообъекта
     * @param {Function} callback - колбэк, который будет применён к геообъекту
     * @private
     */
    _toggleGeoObjects(itemType, callback) {
        this.geoObjectCollectionArray.forEach((geoObject) => {
            if (geoObject.options.get('data').type === itemType) {
                callback(geoObject);
            }
        });
    }

    /**
     * Открывает баллун по клику на кнопку.
     * @param {string} id - дата атрибут из какого-либо объекта
     * @param {string} type - параметр в геообъекте по которому будет производиться поиск
     * @private
     */
    _openBalloon(id, type) {
        const idButton = Number(id);

        this.geoObjectCollection.each((item) => {
            const idMarker = Number(item.options.get(type));

            if (idButton === idMarker) {
                const panel = item.options.get('panel');
                const coords = item.geometry.getCoordinates();

                mapUtils.needBalloonPanel() ? panel.open(item) : item.balloon.open();

                // this._scrollToMap();
                this._setCenterMap(coords);
            }
        });
    }

    /**
     * Скроллит к карте. Пока только на мобилке.
     */
    // _scrollToMap() {
    //     if (mapUtils.isMobile()) {
    //         scrollBy.init();
    //         scrollBy.scrollToElement(this.mapWrapper);
    //     }
    // }

    /**
     * Центрирует карту относительно координат
     * @param {array} coords - координаты центра.
     * @private
     */
    _setCenterMap(coords) {
        if (mapUtils.isMobile()) {
            this.map.setCenter(coords, this.settings.zoom);
        }
    }

    /**
     * 
     * @param {string} pointer - наименование точки на которую необходимо переместить видимую область карты
     */
    _moveMap(pointer) {
        this.map.panTo(this.destinations[pointer], {
            flying: true,
            duration: 1500,
            delay: 2000,
            timingFunction: 'ease-in-out'
        });
        
    }

    _initPolygon() {
        const polygon = new this.ymaps.Polygon([this.settings.polygon],{}, {
            // Убираем видимость обводки.
            fillColor: '#6FAEEE15',
            stroke: true,
            strokeColor: '#6FAEEE'
        });

        this.map.geoObjects.add(polygon);
    }

}

export default YandexMap;
